import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Lock, LockOpen, ImagePlus, X, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function Slot({ url, locked, onUpload, onDelete, editable, testid }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
      await onUpload(data.url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };

  if (!url) {
    if (!editable) {
      return (
        <div
          data-testid={testid}
          className="aspect-[3/4] card-surface flex flex-col items-center justify-center text-zinc-600"
        >
          {locked ? <Lock size={20} /> : <ImagePlus size={20} />}
          <span className="text-[10px] uppercase tracking-widest mt-2">
            {locked ? "Locked" : "Empty"}
          </span>
        </div>
      );
    }
    return (
      <>
        <button
          data-testid={testid}
          type="button"
          onClick={() => input.current?.click()}
          disabled={busy}
          className="aspect-[3/4] card-surface flex flex-col items-center justify-center text-zinc-500 hover:text-rose-500 hover:border-rose-600/50 transition-colors"
        >
          {busy ? <Loader2 size={20} className="animate-spin" /> : <Plus size={20} />}
          <span className="text-[10px] uppercase tracking-widest mt-2">
            {busy ? "Uploading" : "Add photo"}
          </span>
        </button>
        <input ref={input} type="file" accept="image/*" className="hidden" onChange={pick} />
      </>
    );
  }
  return (
    <div data-testid={testid} className="relative aspect-[3/4] overflow-hidden card-surface group">
      <img src={url} alt="" className="w-full h-full object-cover" />
      {editable && (
        <button
          onClick={onDelete}
          className="absolute top-2 right-2 w-8 h-8 bg-black/70 backdrop-blur border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Remove"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}

export default function PhotoGallery({ profile, onChanged }) {
  const { user } = useAuth();
  const isMe = user && user.id === profile.id;
  const [status, setStatus] = useState(profile.private_request_status || null);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => { setStatus(profile.private_request_status || null); }, [profile.private_request_status]);

  const publicSlots = 4;
  const privateSlots = 5;
  const publicPhotos = Array.from({ length: publicSlots }, (_, i) => profile.public_photos?.[i] || "");
  const privatePhotos = Array.from({ length: privateSlots }, (_, i) => profile.private_photos?.[i] || "");

  const setPhoto = async (kind, slot, url) => {
    try {
      await api.put("/users/me/photos", { kind, slot, url });
      onChanged && await onChanged();
    } catch (e) {
      toast.error("Save failed.");
    }
  };
  const removePhoto = async (kind, slot) => {
    try {
      await api.delete(`/users/me/photos/${kind}/${slot}`);
      onChanged && await onChanged();
    } catch (e) {
      toast.error("Delete failed.");
    }
  };

  const requestAccess = async () => {
    if (!user) return toast.error("Log in to request access.");
    setRequesting(true);
    try {
      const { data } = await api.post(`/photos/request/${profile.id}`);
      setStatus(data.status);
      if (data.status === "pending") toast.success("Request sent — waiting for approval.");
      else if (data.status === "granted") toast.success("Access already granted.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not request.");
    } finally {
      setRequesting(false);
    }
  };

  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-[0.3em] text-zinc-500">
        <ImagePlus size={12} /> Public photos <span className="text-zinc-600">·  {publicPhotos.filter(Boolean).length}/4</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {publicPhotos.map((url, i) => (
          <Slot
            key={`pub-${i}`}
            url={url}
            editable={isMe}
            testid={`photo-public-${i}`}
            onUpload={(u) => setPhoto("public", i, u)}
            onDelete={() => removePhoto("public", i)}
          />
        ))}
      </div>

      <div className="flex items-center justify-between mt-8 mb-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-zinc-500">
          {profile.can_view_private ? <LockOpen size={12} className="text-emerald-400" /> : <Lock size={12} className="text-rose-500" />}
          Private photos <span className="text-zinc-600">·  {profile.private_photos_count || 0}/5</span>
        </div>
        {!isMe && !profile.can_view_private && profile.private_photos_count > 0 && (
          <Button
            onClick={requestAccess}
            disabled={requesting || status === "pending"}
            className="btn-crimson rounded-none px-4 py-2 text-xs"
            data-testid="request-private-access-button"
          >
            {status === "pending" ? "Request pending"
             : status === "denied" ? "Try again"
             : requesting ? "Sending…"
             : "Request access"}
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {privatePhotos.map((url, i) => (
          <Slot
            key={`prv-${i}`}
            url={isMe || profile.can_view_private ? url : ""}
            locked={!isMe && !profile.can_view_private}
            editable={isMe}
            testid={`photo-private-${i}`}
            onUpload={(u) => setPhoto("private", i, u)}
            onDelete={() => removePhoto("private", i)}
          />
        ))}
      </div>
      {!isMe && !profile.can_view_private && profile.private_photos_count > 0 && (
        <div className="mt-3 text-xs text-zinc-500 border-l-2 border-rose-600/50 pl-3">
          {status === "pending" && "You'll get a notification when " + profile.display_name + " responds."}
          {status === "denied" && "Access was declined. You can request again."}
          {!status && "Send a request — the owner decides whether to unlock."}
        </div>
      )}
    </div>
  );
}
